import {
  Autocomplete as MuiAutocomplete,
  type AutocompleteProps as MuiAutocompleteProps,
  TextField as MuiTextField,
} from '@mui/material';
import type { ReactNode } from 'react';

import type { WithoutStyleOverrides } from '../../../types/WithoutStyleOverrides';
import { CircularProgress } from '../../atoms/CircularProgress';
import { FormHelperText } from '../../atoms/FormHelperText';
import { Input } from '../../atoms/Input';
import type { TextFieldProps } from '../TextField';

type TextFieldSlotProps = NonNullable<TextFieldProps['slotProps']>;

type FieldSlotProps = {
  [Slot in 'htmlInput' | 'inputLabel']?: Exclude<TextFieldSlotProps[Slot], (...args: never[]) => unknown>;
};

type FieldPart = Pick<
  TextFieldProps,
  'label' | 'helperText' | 'error' | 'required' | 'placeholder' | 'autoFocus' | 'onFocus' | 'type'
> & {
  fieldSlotProps?: FieldSlotProps;
  startAdornment?: ReactNode;
};

type ReplacedPart = 'renderInput' | 'slots' | 'clearIcon' | 'popupIcon';

type Props<
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
> = WithoutStyleOverrides<Omit<MuiAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>, ReplacedPart>>
  & FieldPart;

const FIELD_SLOTS = { input: Input, formHelperText: FormHelperText };

export function Autocomplete<
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(props: Props<Value, Multiple, DisableClearable, FreeSolo>) {
  const {
    label,
    helperText,
    error,
    required,
    placeholder,
    autoFocus,
    onFocus,
    type,
    fieldSlotProps,
    startAdornment,
    ...autocompleteProps
  } = props;

  return (
    <MuiAutocomplete<Value, Multiple, DisableClearable, FreeSolo>
      {...autocompleteProps}
      renderInput={(params) => (
        <MuiTextField
          {...params}
          variant="outlined"
          slots={FIELD_SLOTS}
          label={label}
          helperText={helperText}
          error={error}
          required={required}
          placeholder={placeholder}
          autoFocus={autoFocus}
          onFocus={onFocus}
          type={type}
          slotProps={{
            ...params.slotProps,
            htmlInput: { ...fieldSlotProps?.htmlInput, ...params.slotProps.htmlInput },
            inputLabel: { ...params.slotProps.inputLabel, ...fieldSlotProps?.inputLabel },
            input: {
              ...params.slotProps.input,
              startAdornment:
                startAdornment === undefined
                  ? (
                      params.slotProps.input.startAdornment
                    )
                  : (
                      <>
                        {startAdornment}
                        {params.slotProps.input.startAdornment}
                      </>
                    ),
              endAdornment: (
                <>
                  {autocompleteProps.loading ? <CircularProgress size="small" color="inherit" /> : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}

export type { Props as AutocompleteProps };
